import type { GridCellKey } from '../cell.types'

import { ref, type Snapshot } from 'valtio'
import { getDefaultStore } from 'jotai'

import { gridState, gridStateActions } from '../../grid/grid.store'

import * as logger from '@/utils/logger'
import { formatBytes } from '@/utils/files'
import { getErrorMessage, isAbortError } from '@/utils/errors'

import { IMAGE_TYPES, MAX_FILE_SIZE, MAX_FREE_FILE_SIZE } from '@/constants'
import { Features } from '@rundown-studio/types'

import { uploadRundownFile } from '@/entities/rundown'
import { rundownPageStore } from '../../rundown-page.store'
import { planAtom } from '@/store/plan.store'

import { upsertCell } from '../cell.requests'

import { createFileBlock } from '../content/tiptap/file/FileNode'
import { createImageBlock } from '../content/tiptap/image/ImageNode'
import {
  createJSONContentFromHTMLContent,
  renderJSONContentAsHTMLString,
} from '../content/tiptap/tiptap-helpers'
import { getContentForTextCell } from '../content/content.helpers'
import { EMPTY_PARAGRAPH_JSON } from '../content/tiptap/tiptap-config'
import { triggerToast } from '@/shared/ui/toast'

// #region ============== TYPES

// =============== FILE

type FileStateBase = {
  /** Temp. generated id for keying */
  id: string
  // Pick fields from `File`
  readonly name: File['name']
  readonly type: File['type']
  readonly size: File['size']
}

type FileStateUploading = FileStateBase & {
  status: 'uploading'
  progress: number
}

type FileStateCompleted = FileStateBase & {
  status: 'completed'
  /** Cloud asset file path */
  url: string
}

type FileStateFailed = FileStateBase & {
  status: 'failed'
  error: string
}

/** Discriminated union of possible states of a file */
type FileState = FileStateUploading | FileStateCompleted | FileStateFailed

// =============== CELL UPLOAD

/** Each active upload has a map of files */
type CellUploadFilesMap = Record<string, FileState>

type CellUploadStateBase = {
  filesMap: CellUploadFilesMap // Always available because files have own state
}

type CellUploadStateUploading = CellUploadStateBase & {
  status: 'uploading'
  /** Cached AbortController. DO NOT access this in snapshot, only in state. */
  controllerRef?: AbortController
}

type CellUploadSuccessState = CellUploadStateBase & {
  status: 'success'
}

type CellUploadErrorState = CellUploadStateBase & {
  status: 'error'
  error: string
}

/** Discriminated union of upload state for each cell */
type CellUploadState =
  | CellUploadStateUploading
  | CellUploadSuccessState
  | CellUploadErrorState

// =============== CELL UPLOAD REGISTRY

/**
 * Registry that holds nested state for all multi-file uploads
 */
export type CellUploadRegistry = Record<string, CellUploadState | null>

/** Cell context object (required by `upsertCell` function) */
type CellMeta = {
  cell: Parameters<typeof upsertCell>[0]
  columnId: Parameters<typeof upsertCell>[1]
  cueId: Parameters<typeof upsertCell>[2]
}

// #endregion

/**
 * Upload handler for files dragged onto Cell Preview's.
 *
 * Each cell's state has an entry in the gridState's `cellUploadRegistry`.
 * This lets us track progress, files, and errors separately for each cell
 * so users can upload multiple files at a time to multiple cells.
 */
async function handleCellUpload(
  cellKey: GridCellKey,
  files: File[],
  cellMeta: CellMeta,
) {
  if (!files || files.length === 0) return

  logger.log('[CellUpload] Starting for', cellKey)

  // Cancel any active uploads first
  cellUploadActions.cancelCellUpload(cellKey)

  // =============== Step 1. Prepare state and files
  const parentController = new AbortController()
  const { signal: parentSignal } = parentController
  const timestamp = Date.now()

  // Initialize state
  gridState.cellUploadRegistry[cellKey] = {
    status: 'uploading',
    filesMap: {},
    controllerRef: ref(parentController),
  }

  /** Alias for filesMap */
  const filesMapProxy = gridState.cellUploadRegistry[cellKey].filesMap

  // =============== Step 2. Prepare all files in batch
  const uploadTaskPromises = files.map(async (file, index) => {
    /** Simple file id to use as key instead of index */
    const id = `file_${timestamp}_${index}`

    // Create isolated AbortController per file
    const fileController = new AbortController()
    const { signal: fileSignal } = fileController

    // Sync file controller to parent cancellation
    parentController.signal.addEventListener(
      'abort',
      () => fileController.abort(),
      { once: true },
    )

    // Step 2.1: Initialize file
    filesMapProxy[id] = {
      id: id,
      status: 'uploading',
      progress: 0,
      // Copy native file fields
      name: file.name,
      type: file.type,
      size: file.size,
    }

    /** Alias for file */
    const fileProxy = filesMapProxy[id]

    // Step 2.2: Check file size against limit
    if (file.size > MAX_FILE_SIZE) {
      setFileToFailed(`Too big (Max. ${formatBytes(MAX_FILE_SIZE)})`)

      fileController.abort()
      fileSignal.throwIfAborted()
    }

    // Step 2.3: Check file size against free user limit
    const plan = getDefaultStore().get(planAtom)
    const isFreeUser = !plan?.features.includes(Features.BASIC)

    if (isFreeUser && file.size > MAX_FREE_FILE_SIZE) {
      setFileToFailed(`Too big (Max. ${formatBytes(MAX_FREE_FILE_SIZE)})`)

      rundownPageStore.modals.upgradeModal?.show()

      fileController.abort()
      fileSignal.throwIfAborted()
    }

    // 2.4 Upload each file to cloud storage and track errors
    try {
      // Don't start if already aborted
      fileSignal.throwIfAborted()

      // Prepare request
      const response = await uploadRundownFile({
        rundownId: rundownPageStore.rundown.id,
        payload: {
          file: file,
        },
        signal: parentSignal,
        onProgress(progress) {
          if (fileSignal.aborted) return
          fileProxy.progress = progress
        },
      })

      // Check asset url
      const uploadedFileUrl = response.data.url
      if (!uploadedFileUrl) throw new Error('Invalid response')

      // Set final file state
      filesMapProxy[id] = {
        ...fileProxy,
        status: 'completed',
        url: uploadedFileUrl,
      }
    } catch (err: unknown) {
      fileController.abort()

      // Handle cancellation differently from other errors
      if (isAbortError(err)) {
        setFileToFailed('Cancelled')
      } else {
        const message = getErrorMessage(err)
        setFileToFailed(message)
      }

      throw err
    }

    /** Internal helper for setting failed state */
    function setFileToFailed(error: string) {
      filesMapProxy[id] = {
        ...fileProxy,
        status: 'failed',
        error: error,
      }
    }
  })

  // =============== Step 3. Process batch
  try {
    // Execute tasks in batch concurrently
    await Promise.allSettled(uploadTaskPromises)

    // If upload was canceled, abort
    if (parentSignal.aborted) {
      setUploadToFailed(cellKey, 'Cancelled.')
      return
    }

    // Check final statuses
    const currentFiles = Object.values(filesMapProxy)

    const hasSomeCompleted = currentFiles.some((f) => f.status === 'completed')

    // Update the parent status based on the batch outcomes
    if (hasSomeCompleted) {
      logger.log('[CellUpload] Completed for', cellKey)

      setUploadToCompleted(cellKey)

      saveCompletedUploadsToCellContent(cellKey, cellMeta)
    } else {
      throw new Error('All uploads failed.')
    }
  } catch (err) {
    triggerToast({
      type: 'fail',
      title: `Uploading failed.`,
      body: 'Please refresh and try again.',
    })

    const message = getErrorMessage(err)
    console.error(message)

    setUploadToFailed(cellKey, `Upload failed.`)
  } finally {
    // Always clean up memory
    cleanUpCellController(cellKey)
  }
}

/**
 * Function responsible for updating a cell's content after files are upload.
 * It processes the files, converts them to custom nodes and safely appends
 * them to existing content.
 */
async function saveCompletedUploadsToCellContent(
  cellKey: GridCellKey,
  cellMeta: CellMeta,
) {
  const currentUpload = gridState.cellUploadRegistry[cellKey]

  if (currentUpload?.status !== 'success') {
    console.error('Invalid status')
    return
  }

  // Get files and filter out those that failed
  const allFiles = Object.values(currentUpload.filesMap)
  const completedFiles = allFiles.filter((file) => file.status === 'completed')

  // Map files to custom nodes based on file type
  const newNodes = completedFiles.map((file) => {
    if (IMAGE_TYPES.includes(file.type)) {
      // Image node
      return createImageBlock({
        src: file.url,
      })
    } else {
      // File node
      return createFileBlock({
        filename: file.name,
        type: file.type,
        size: formatBytes(file.size),
        url: file.url,
      })
    }
  })

  // Handle situations where editor is active
  const isEditing = gridState.editingCell === cellKey

  const contentAsHTML = (() => {
    if (isEditing) {
      // Get latest content with potential changes
      const latestContent = gridStateActions.editor.getEditorContent()

      // Reset and discard editor session after capturing content
      gridStateActions.editor.discard()

      return latestContent
    } else {
      return getContentForTextCell(cellMeta.cell?.content)
    }
  })()

  // Convert to JSON for safer manipulation
  const contentAsJSON = createJSONContentFromHTMLContent(contentAsHTML)
  const updatedContentAsJSON = structuredClone(contentAsJSON)

  // Append new nodes safely
  updatedContentAsJSON.content.push(...newNodes)

  // Append trailing <p>
  updatedContentAsJSON.content.push(EMPTY_PARAGRAPH_JSON)

  // Convert back to HTML for storage
  const updatedContentAsHTML =
    renderJSONContentAsHTMLString(updatedContentAsJSON)

  const contentPayload = {
    content: {
      text: updatedContentAsHTML,
    },
  }

  // TODO: No error handling because upsertCell swallows errors.
  await upsertCell(
    cellMeta.cell,
    cellMeta.columnId,
    cellMeta.cueId,
    contentPayload,
  )

  logger.log('[CellUpload] Content updated for', cellKey)

  cellUploadActions.clearCellUpload(cellKey)
}

//
// =============== STATE HELPERS
//
function setUploadToCompleted(cellKey: GridCellKey) {
  const currentUpload = gridState.cellUploadRegistry[cellKey]
  if (currentUpload?.status !== 'uploading') return

  // Replace object so TS understands the new type
  gridState.cellUploadRegistry[cellKey] = {
    ...currentUpload,
    status: 'success',
  }
}

function setUploadToFailed(cellKey: GridCellKey, error: string) {
  const currentUpload = gridState.cellUploadRegistry[cellKey]

  // Replace object so TS understands the new type
  gridState.cellUploadRegistry[cellKey] = {
    ...(currentUpload as CellUploadState),
    status: 'error',
    error: error,
  }
}

/**
 * Internal cleanup function
 */
function cleanUpCellController(cellKey: GridCellKey) {
  const currentUpload = gridState.cellUploadRegistry[cellKey]
  if (currentUpload?.status !== 'uploading') return

  // Drop reference immediately
  delete currentUpload.controllerRef
}

/**
 * Encapsulate and export cellUpload actions so they don't pollute the gridState file.
 */
export const cellUploadActions = {
  startCellUpload: handleCellUpload,

  cancelCellUpload(cellKey: GridCellKey) {
    const currentUpload = gridState.cellUploadRegistry[cellKey]
    if (currentUpload?.status !== 'uploading') return

    // Check properly for un-proxied controller reference
    if (!currentUpload.controllerRef?.abort) return

    // Triggers the cancellation cascade safely
    currentUpload.controllerRef.abort()

    cleanUpCellController(cellKey)
  },

  clearCellUpload(cellKey: GridCellKey) {
    // Reset upload state
    gridState.cellUploadRegistry[cellKey] = null
  },
}

/**
 * UI helper that calculates upload progress.
 *
 * Supports multiple simultaneous uploads, it reduces each files progress
 * into a total percentage for the whole batch. (Excludes failed files)
 */
export function getCellUploadProgress(
  snapshot: Snapshot<typeof gridState>,
  cellId: string,
) {
  const uploadState = snapshot.cellUploadRegistry[cellId]

  // Early return if the state doesn't match an active upload structure
  if (
    !uploadState ||
    uploadState.status !== 'uploading' ||
    !uploadState.filesMap
  ) {
    return {
      totalFilesCount: 0,
      totalProgress: 0,
      isTransferring: false,
    }
  }

  const uploadingFileIds = Object.values(uploadState.filesMap)
    .filter((state) => state.status !== 'failed')
    .map((state) => state.id)

  const totalFilesCount = uploadingFileIds.length

  let totalProgressSum = 0
  let isTransferring = false

  for (const id of uploadingFileIds) {
    const file = uploadState.filesMap[id]
    if (file && 'progress' in file) {
      totalProgressSum += file.progress
      if (file.progress > 0) {
        isTransferring = true
      }
    }
  }

  return {
    totalFilesCount,
    totalProgress: totalFilesCount
      ? Math.round(totalProgressSum / totalFilesCount)
      : 0,
    isTransferring,
  }
}
